import type { Factor, Session } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { supabase } from "../services/supabase/Supabase";

type Props = {
  children: (session: Session) => React.ReactNode;
};

type Stage =
  | { kind: "loading" }
  | { kind: "signed-out" }
  | { kind: "mfa-required"; factor: Factor; session: Session }
  | { kind: "authed"; session: Session }
  | { kind: "error"; message: string };

export function SignInGate({ children }: Props) {
  const [stage, setStage] = useState<Stage>({ kind: "loading" });

  useEffect(() => {
    resolve();
    const { data: sub } = supabase.auth.onAuthStateChange((evt, session) => {
      // Only react to sign-outs. Post-sign-in and post-MFA-verify transitions
      // are driven explicitly by the forms to avoid racing against resolve().
      if (evt === "SIGNED_OUT" || !session) {
        setStage({ kind: "signed-out" });
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const resolve = async () => {
    try {
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      const session = sessionData.session;
      if (!session) {
        setStage({ kind: "signed-out" });
        return;
      }

      const { data: aal, error: aalError } =
        await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalError) throw aalError;

      if (aal?.currentLevel === "aal1" && aal.nextLevel === "aal2") {
        const { data: factors, error: factorsError } =
          await supabase.auth.mfa.listFactors();
        if (factorsError) throw factorsError;
        const factor = factors?.totp?.[0];
        if (factor) {
          setStage({ kind: "mfa-required", factor, session });
          return;
        }
      }

      setStage({ kind: "authed", session });
    } catch (err) {
      console.error("SignInGate resolve failed", err);
      setStage({
        kind: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const markAuthed = async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session) setStage({ kind: "authed", session: data.session });
  };

  switch (stage.kind) {
    case "loading":
      return <Shell title="Loading…"><p className="signin-loading">Checking session…</p></Shell>;
    case "signed-out":
      return <PasswordForm onSignedIn={resolve} />;
    case "mfa-required":
      return <MfaForm factor={stage.factor} onVerified={markAuthed} />;
    case "authed":
      return <>{children(stage.session)}</>;
    case "error":
      return (
        <Shell title="Something went wrong">
          <p className="signin-error">{stage.message}</p>
          <button
            type="button"
            className="signin-submit"
            onClick={async () => {
              await supabase.auth.signOut();
              setStage({ kind: "loading" });
              resolve();
            }}
          >
            Retry
          </button>
        </Shell>
      );
  }
}

function PasswordForm({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        setError(error.message);
        return;
      }
      await onSignedIn();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell title="Sign in" subtitle=" ">
      <form onSubmit={handleSubmit}>
        <div className="signin-field">
          <label>Email</label>
          <input type="email" placeholder="name@gomarloo.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="signin-field">
          <label>Password</label>
          <input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <button type="submit" className="signin-submit" disabled={submitting}>
          {submitting ? "Signing in…" : "Sign in"}
        </button>
        {error ? <p className="signin-error">{error}</p> : null}
      </form>
    </Shell>
  );
}

function MfaForm({
  factor,
  onVerified,
}: {
  factor: Factor;
  onVerified: () => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: factor.id,
        code: code.trim(),
      });
      if (error) {
        setError(error.message);
        return;
      }
      await onVerified();
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async () => {
    await supabase.auth.signOut();
  };

  return (
    <Shell title="Two-factor authentication" subtitle="Enter the code from your authenticator app.">
      <form onSubmit={handleSubmit}>
        <div className="signin-field">
          <label>Authentication code</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={8}
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            required
            autoFocus
            className="mono"
          />
        </div>
        <button type="submit" className="signin-submit" disabled={submitting}>
          {submitting ? "Verifying…" : "Verify"}
        </button>
        <button type="button" className="signin-cancel" onClick={cancel}>
          Cancel — sign out
        </button>
        {error ? <p className="signin-error">{error}</p> : null}
      </form>
    </Shell>
  );
}

function Shell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="signin-root">
      <div className="signin-wrap">
        <div className="signin-brand">
          <img src="/marloo-logo.svg" alt="Marloo" className="wordmark" />
          <p>Internal Dashboards</p>
        </div>
        <div className="signin-card">
          <p className="signin-card-title">{title}</p>
          {subtitle && <p className="signin-card-sub">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}
